import { FC } from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';

import { SafariPinnedTab } from '@zoopla/assets';

import {
    OpenGraphItems,
    OpenGraphItem,
    ExtraLinkItems,
    ExtraLinkItem,
    ExtraMetaTags
} from './types';
import { SOCIAL_SETTINGS } from './socialSettings';
import * as React from 'react';

export const buildCanonicalUrl = (path: string) => {
    const pathWithoutForwardSlashPrefix = path.replace(/^\//g, '');

    return `https://www.zoopla.co.uk/${pathWithoutForwardSlashPrefix}`;
};

export const getSocialSharingTags = (path: string) => {
    const iPhoneUrl = `${SOCIAL_SETTINGS.IPHONE_BASE_URL}${path}`;
    const twitterPlayUrl = `${SOCIAL_SETTINGS.TWITTER_PLAY_BASE_URL}${path}`;
    return (
        <>
            <meta name="twitter:card" content="summary" />
            <meta
                name="twitter:app:name:iphone"
                content={SOCIAL_SETTINGS.TWITTER_IOS_APP_NAME}
            />
            <meta
                name="twitter:app:id:iphone"
                content={SOCIAL_SETTINGS.IOS_APP_ID}
            />
            <meta
                name="twitter:app:name:googleplay"
                content={SOCIAL_SETTINGS.TWITTER_ANDROID_APP_NAME}
            />
            <meta
                name="twitter:app:id:googleplay"
                content={SOCIAL_SETTINGS.ANDROID_APP_ID}
            />
            <meta name="twitter:app:url:iphone" content={iPhoneUrl} />
            <meta name="twitter:app:url:googleplay" content={twitterPlayUrl} />
            <meta property="fb:admins" content={SOCIAL_SETTINGS.FB_ADMINS} />
            <meta
                property="twitter:account_id"
                content={SOCIAL_SETTINGS.TWITTER_ACCOUNT_ID}
            />
        </>
    );
};

export const generateOpenGraphTags = (items: OpenGraphItems) => (
    <>
        {items.map(({ property, content }: OpenGraphItem) => (
            <meta
                key={`${property}-${content}`}
                property={property}
                content={content}
            />
        ))}
    </>
);

export const generateExtraLinkTags = (items: ExtraLinkItems) => (
    <>
        {items.map(({ href, rel, crossOrigin }: ExtraLinkItem) => (
            <link key={href} href={href} rel={rel} crossOrigin={crossOrigin} />
        ))}
    </>
);

export const generateRobots = (shouldIndex: Index, shouldFollow: boolean) => {
    if (shouldIndex() && !shouldFollow) {
        return <meta name="robots" content="index, nofollow" />;
    } else if (!shouldIndex() && shouldFollow) {
        return <meta name="robots" content="noindex, follow" />;
    } else if (!shouldIndex() && !shouldFollow) {
        return <meta name="robots" content="noindex, nofollow" />;
    }

    return null;
};

/* custom css overrides to hide spinner on 1st slide and hide img alt text, unfortunately text-indent tricks don't work here */
const customSplideOverrides = `[id$=-slide01] .splide__spinner{display:none}a .splide__slide img{font-size:0}`;
// previously hosted at: https://cdn.prod.zoopla.co.uk/splide-core.min.css
export const splideCoreMinCss = `.splide.is-initialized:not(.is-active) .splide__list,.splide__track--ttb>.splide__list{display:block}.splide__container,.splide__slide{box-sizing:border-box;position:relative}@keyframes splide-loading{0%{transform:rotate(0)}to{transform:rotate(1turn)}}.splide__track--draggable{-webkit-touch-callout:none;-webkit-user-select:none;-ms-user-select:none;user-select:none}.splide__track--fade>.splide__list>.splide__slide{margin:0!important;opacity:0;z-index:0}.splide__track--fade>.splide__list>.splide__slide.is-active{opacity:1;z-index:1}.splide--rtl{direction:rtl}.splide__list{backface-visibility:hidden;display:-ms-flexbox;display:flex;height:100%;margin:0!important;padding:0!important}.splide__pagination{-ms-flex-align:center;align-items:center;display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap;-ms-flex-pack:center;justify-content:center;margin:0;pointer-events:none}.splide__pagination li{display:inline-block;line-height:1;list-style-type:none;margin:0;pointer-events:auto}.splide:not(.is-overflow) .splide__pagination,.splide__toggle.is-active .splide__toggle__play,.splide__toggle__pause{display:none}.splide__progress__bar{width:0}.splide{position:relative;visibility:hidden}.splide.is-initialized,.splide.is-rendered{visibility:visible}.splide__slide{backface-visibility:hidden;-ms-flex-negative:0;flex-shrink:0;list-style-type:none!important;margin:0}.splide__slide img{vertical-align:bottom}.splide__spinner{animation:1s linear infinite splide-loading;border:2px solid #999;border-left-color:transparent;border-radius:50%;bottom:0;contain:strict;display:inline-block;height:20px;left:0;margin:auto;position:absolute;right:0;top:0;width:20px}.splide__sr{clip:rect(0 0 0 0);border:0;height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px}.splide__toggle.is-active .splide__toggle__pause{display:inline}.splide__track{overflow:hidden;position:relative;z-index:0}${customSplideOverrides}`;

type Index = () => boolean;
export type Props = {
    pageTitle: string;
    pageDescription: string;
    canonicalPath?: string;
    index?: Index;
    follow?: boolean;
    schema?: () => string;
    renderSocialSharingTags?: boolean;
    openGraphValues?: OpenGraphItems;
    extraLinkTags?: ExtraLinkItems;
    extraMetaTags?: ExtraMetaTags;
};

export const Head: FC<React.PropsWithChildren<Props>> = ({
    pageTitle,
    pageDescription,
    canonicalPath,
    index = () => true,
    follow = true,
    schema,
    renderSocialSharingTags,
    openGraphValues,
    extraLinkTags,
    children,
    extraMetaTags
}) => {
    const router = useRouter();
    const { asPath } = router;

    return (
        <NextHead>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            {canonicalPath && (
                <link rel="canonical" href={buildCanonicalUrl(canonicalPath)} />
            )}
            <link
                rel="apple-touch-icon"
                sizes="180x180"
                href="/apple-touch-icon.png"
            />
            <link
                rel="icon"
                type="image/png"
                href="/favicon-32x32.png"
                sizes="32x32"
            />
            <link
                rel="icon"
                type="image/png"
                href="/favicon-16x16.png"
                sizes="16x16"
            />
            <link rel="shortcut icon" href="/favicon.ico" />
            <link rel="manifest" href="/site.webmanifest" />
            <link rel="mask-icon" href={SafariPinnedTab} color="#8046f1" />

            {/* inline css to avoid render blocking network call */}
            <style>{splideCoreMinCss}</style>
            {extraLinkTags ? generateExtraLinkTags(extraLinkTags) : null}
            <meta name="msapplication-TileColor" content="#8046f1" />
            <meta name="theme-color" content="#ffffff" />
            {renderSocialSharingTags ? getSocialSharingTags(asPath) : null}
            {openGraphValues && openGraphValues.length > 0
                ? generateOpenGraphTags(openGraphValues)
                : null}
            {generateRobots(index, follow)}
            {extraMetaTags
                ? extraMetaTags.map(({ name, content }, index) => (
                      <meta
                          key={`${name}-${index}`}
                          name={name}
                          content={content}
                      />
                  ))
                : null}
            {schema ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: schema()
                    }}
                />
            ) : null}
            {children}
        </NextHead>
    );
};
